import { useEffect, useState, useRef } from 'react'
import { bulkDeleteChecklistSuggestions, createChecklistSuggestion, deleteChecklistSuggestion, getChecklistSuggestions, updateChecklistSuggestion } from '../../utils/urls'
import axios from '../../utils/axios';
import { InputText } from 'primereact/inputtext';
import FlexComponent from '../FlexComponent';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog } from '../../shared/ConfirmModal';
import { Checkbox } from 'primereact/checkbox';

function ChecklistSuggestions({ toast }) {
    const [title, setTitle] = useState('')
    const [editTitle, setEditTitle] = useState('')

    const [isAddSuggestion, setIsAddSuggestion] = useState(false)
    const [suggestionsList, setSuggestionsList] = useState([])
    const [suggestionEditIndex, setSuggestionEditIndex] = useState()
    const [isSuggestionEdit, setIsSuggestionEdit] = useState()

    const [loading, setLoading] = useState(true)
    const [isMounted, setIsMounted] = useState(false)

    // PAGINATION + SEARCH
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [search, setSearch] = useState('')
    const [selectedSuggestions, setSelectedSuggestions] = useState([]);

    const debounceRef = useRef(null)

    const handleAddSuggestion = () => {
        setIsMounted(true)
        setIsAddSuggestion(false)

        axios.post(createChecklistSuggestion, { name: title })
            .then((response) => {
                if (response.status === 201) {
                    setTitle('')
                    toast.current.show({ severity: 'success', summary: 'Success', life: 3000 });
                    setIsMounted(false)
                } else throw new Error()
            })
            .catch(() => {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
                setIsMounted(false)
            })
    }

    const handleEditSubmit = (id) => {
        setIsSuggestionEdit(false)
        setIsMounted(true)

        axios.put(`${updateChecklistSuggestion}${id}/`, { name: editTitle })
            .then((response) => {
                if (response.status === 200) {
                    toast.current.show({ severity: 'success', summary: 'Success', life: 3000 });
                    setIsMounted(false)
                } else throw new Error()
            })
            .catch(() => {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
                setIsMounted(false)
            })
    }

    const handleClick = () => {
        setTitle('')
        setIsAddSuggestion(!isAddSuggestion)
        setIsSuggestionEdit(false);
    }

    const handleEdit = (index) => {
        setIsAddSuggestion(false)
        setIsSuggestionEdit(true);
        setSuggestionEditIndex(index);
        setEditTitle(suggestionsList[index].name);
    };

    // API CALL
    const getAllSuggestions = (pageNumber = 1, isSearch = false) => {
        setLoading(true)

        let url = `${getChecklistSuggestions}?limit=20&page=${pageNumber}`

        if (search.trim()) {
            url += `&search=${search}`
        }

        axios.get(url)
            .then((response) => {
                if (response.status === 200) {
                    const results = response.data.results || []

                    if (pageNumber === 1 || isSearch) {
                        setSuggestionsList(results)
                    } else {
                        setSuggestionsList(prev => [...prev, ...results])
                    }

                    setHasMore(results.length === 20)
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false))
    }

    const handleLoadMore = () => {
        const nextPage = page + 1
        setPage(nextPage)
        getAllSuggestions(nextPage)
    }

    const handleClearSearch = () => {
        setSearch('')
        setPage(1)
        setIsMounted(prev => !prev)
    }

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        debounceRef.current = setTimeout(() => {
            setPage(1)
            getAllSuggestions(1, true)
        }, 400)

        return () => clearTimeout(debounceRef.current)
    }, [search])

    useEffect(() => {
        getAllSuggestions(page)
    }, [isMounted])

    const handleDelete = (list) => {
        setIsMounted(true)
        confirmDialog({
            message: 'Are you sure you want to delete this Suggestion?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                axios.delete(`${deleteChecklistSuggestion}${list.id}/`)
                    .then((response) => {
                        // if (response.status === 200) {
                        setIsMounted(false)
                        // } else throw new Error()
                    })
                    .catch(() => {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
                        setIsMounted(false)
                    })
            }
        });
    };

    // console.log("selectedSuggestions", selectedSuggestions);

    const handleBulkDelete = () => {
        if (!selectedSuggestions.length) return;

        confirmDialog({
            message: 'Are you sure you want to delete selected suggestions?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    setIsMounted(true);
                    await axios.delete(bulkDeleteChecklistSuggestions, {
                        data: { ids: selectedSuggestions }
                    });

                    setSelectedSuggestions([]);
                    toast.current.show({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Suggestions deleted successfully',
                        life: 3000
                    });

                } catch (err) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Bulk delete failed',
                        life: 3000
                    });
                } finally {
                    setIsMounted(false);
                }
            }
        });
    };

    return (
        <div>

            {/* LIVE SEARCH */}
            <FlexComponent gap="0.5rem" style={{ marginBottom: "1rem", marginTop: "1rem" }}>
                <InputText
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: "100%" }}
                />
                {search && (
                    <Button
                        style={{ background: "black", color: "white" }}
                        onClick={handleClearSearch}
                    >
                        X
                    </Button>
                )}
            </FlexComponent>

            <FlexComponent>
                <Button style={{ marginTop: ".5rem" }} onClick={handleClick}>
                    Add Suggestion
                </Button>

                {selectedSuggestions?.length > 0 && <Button
                    style={{ marginTop: ".5rem", marginLeft: ".5rem", background: "red", color: "white" }}
                    disabled={!selectedSuggestions.length}
                    onClick={handleBulkDelete}
                >
                    Delete Selected ({selectedSuggestions.length})
                </Button>}
            </FlexComponent>

            {isAddSuggestion && (
                // <FlexComponent style={{ marginTop: "1rem", border: "1px solid grey", padding: "1rem", borderRadius: "15px" }} flexDirection="column" gap='.5rem'>
                //     <InputText value={title} onChange={(e) => setTitle(e.target.value)} />
                //     <FlexComponent gap="1rem">
                //         <Button onClick={handleAddSuggestion} disabled={!title.trim()}>Save</Button>
                //         <Button onClick={() => setIsAddSuggestion(false)}>Cancel</Button>
                //     </FlexComponent>
                // </FlexComponent>
                <FlexComponent style={{ marginTop: "1rem", border: "1px solid grey", padding: "1rem", borderRadius: "15px" }} className='inputFlexContainer' flexDirection="column" gap='.5rem' alignItems='center'>

                    <div className='inputContainerDiv' style={{ width: '100%' }}>
                        <label htmlFor="email" style={{ fontWeight: 'bold' }}>Title *</label>
                        <InputText size="small" style={{ width: '100%' }} id="title" value={title}
                            onChange={(e) => {
                                setTitle(e.target.value)
                            }}
                        />
                    </div>

                    <FlexComponent justifyContent="center" gap="1rem" >
                        <Button type='button' onClick={handleAddSuggestion} disabled={!title.trim()} >Save</Button>
                        <Button style={{ background: "white", color: "black" }} type='button' onClick={() => {
                            setIsAddSuggestion(false)
                        }} >Cancel</Button>
                    </FlexComponent>
                </FlexComponent>
            )}

            {/* LIST */}
            {suggestionsList.map((list, index) => (
                <div key={index} style={{ marginTop: ".5rem" }}>
                    {!isSuggestionEdit || suggestionEditIndex !== index ? (
                        <FlexComponent gap="1rem">
                            <Checkbox
                                checked={selectedSuggestions.includes(list.id)}
                                onChange={(e) => {
                                    if (e.checked) {
                                        setSelectedSuggestions(prev => [...prev, list.id]);
                                    } else {
                                        setSelectedSuggestions(prev =>
                                            prev.filter(id => id !== list.id)
                                        );
                                    }
                                }}
                            />
                            <p style={{ width: "100%" }}>{list.name}</p>
                            <Button onClick={() => handleEdit(index)} icon="pi pi-pencil" />
                            <Button onClick={() => handleDelete(list)} icon="pi pi-trash" />
                        </FlexComponent>
                    ) : (
                        <FlexComponent style={{ marginTop: "1rem", border: "1px solid grey", padding: "1rem", borderRadius: "15px" }} className='inputFlexContainer' flexDirection="column" gap='.5rem' alignItems='center'>

                            <div className='inputContainerDiv' style={{ width: '100%' }}>
                                <label htmlFor="email" style={{ fontWeight: 'bold' }}>Title *</label>
                                <InputText size="small" style={{ width: '100%' }} id="title" value={editTitle}
                                    onChange={(e) => {
                                        setEditTitle(e.target.value)
                                    }}
                                />
                            </div>

                            <FlexComponent justifyContent="center" gap="1rem" >
                                <Button type='button' onClick={() => {
                                    handleEditSubmit(list.id)
                                }} disabled={!editTitle.trim()} >Update</Button>
                                <Button style={{ background: "white", color: " black" }} type='button' onClick={() => {
                                    setIsSuggestionEdit(false)
                                }} >Discard Changes</Button>
                            </FlexComponent>
                        </FlexComponent>
                    )}
                </div>
            ))}

            {/* LOADER */}
            {loading && (
                <FlexComponent justifyContent="center">
                    <ProgressSpinner />
                </FlexComponent>
            )}

            {/* LOAD MORE */}
            {!loading && hasMore && suggestionsList.length > 0 && (
                <FlexComponent justifyContent="center" style={{ marginTop: "1rem" }}>
                    <Button onClick={handleLoadMore}>Load More</Button>
                </FlexComponent>
            )}
        </div>
    )
}

export default ChecklistSuggestions